import type { Media, MediaData } from "api/schemas";

export type sizePrefix = 50 | 100 | 240 | 320 | 480 | 720 | 1080 | 1440 | 2048;

export const IMAGE_QUALITY = 65;

type SignedUrlObj = {
  data: MediaData;
};

/**
 * Constructs an image URL from a Media, ensuring the correct format.
 *
 * @param media - The media object containing image data.
 * @param options - Configuration options.
 * @param options.preserveExtension - If true, preserves the original file extension.
 * @param options.width - The width (pixels - size prefix) for the image.
 * @param options.quality - The AVIF quality suffix (e.g. 60 → `_60`). Defaults to IMAGE_QUALITY.
 * @returns A valid image URL or null if the data is insufficient.
 */
export const constructImageUrl = (
  media: Media | null | undefined,
  options: {
    preserveExtension?: boolean;
    width?: sizePrefix;
    extension?: string;
    quality?: number;
  } = {} // Default options to prevent undefined errors
): string | null => {
  if (!media?.data) return null;

  const { CFURL, name, signature } = media.data;
  const {
    preserveExtension = false,
    width = 1080,
    extension = "avif",
    quality = IMAGE_QUALITY,
  } = options;

  if (!CFURL || !name || !signature) return null;

  const imageName =
    media.media_type === "video" || preserveExtension || name.endsWith(".avif")
      ? name
      : `${name}.${extension || "avif"}_${quality}`;

  // omit width if media is video
  if (media.media_type === "video") {
    return `${CFURL}${imageName}${signature}`;
  }

  // Construct the URL in the required format
  return `${CFURL}${width}_${imageName}${signature}`;
};

export function constructSignedUrl(signedUrlPayload: SignedUrlObj): string {
  const { CFURL, name, signature } = signedUrlPayload.data;

  return `${CFURL}${name}${signature}`;
}
